import { useState } from 'react';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Field';

// Admin-only settings panel (webapp): invite a user by email, optionally
// granting them admin, and get back a magic link to copy/send. There's no
// email delivery — links are shared manually, same as the admin CLI. Only
// rendered when /api/me reports is_admin, and the server re-checks on POST.
interface InviteResult {
  email: string;
  url: string;
  expires_at: number;
  user_created: boolean;
  is_admin: boolean;
}

// Preset link lifetimes, in minutes. "Custom" reveals a number + unit input.
const EXPIRY_PRESETS: { label: string; minutes: number }[] = [
  { label: '1 hour', minutes: 60 },
  { label: '12 hours', minutes: 720 },
  { label: '1 day', minutes: 1440 },
  { label: '3 days', minutes: 4320 },
  { label: '1 week', minutes: 10080 },
  { label: '30 days', minutes: 43200 },
];

export function InvitePanel() {
  const [email, setEmail] = useState('');
  const [makeAdmin, setMakeAdmin] = useState(false);
  const [preset, setPreset] = useState<number | 'custom'>(1440);
  const [customValue, setCustomValue] = useState(2);
  const [customUnit, setCustomUnit] = useState<'hours' | 'days'>('days');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function expiresInMinutes(): number {
    if (preset !== 'custom') return preset;
    const n = Math.max(1, Math.floor(customValue || 0));
    return customUnit === 'days' ? n * 1440 : n * 60;
  }

  async function invite() {
    const e = email.trim();
    if (!e || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      const r = await fetch('/api/admin/invite', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: e,
          make_admin: makeAdmin,
          expires_in_minutes: expiresInMinutes(),
        }),
      });
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `request failed (${r.status})`);
      }
      setResult((await r.json()) as InviteResult);
      setEmail('');
      setMakeAdmin(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the link is selectable in the field as a fallback.
    }
  }

  return (
    <Card className="space-y-3">
      <h2 className="text-lg font-semibold">Invite</h2>
      <p className="text-xs text-slate-600">
        Issue a sign-in link for someone (created if they're new). There's no email — copy the
        link and send it to them. Links expire in 24 hours.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void invite();
          }}
          placeholder="person@example.com"
          className="min-w-0 flex-1"
        />
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={makeAdmin}
            onChange={(e) => setMakeAdmin(e.target.checked)}
          />
          make admin
        </label>
        <Button
          variant="primary"
          onClick={() => void invite()}
          disabled={email.trim() === ''}
          loading={busy}
        >
          {busy ? 'Inviting…' : 'Invite'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span>Expires in</span>
        <Select
          size="sm"
          value={preset === 'custom' ? 'custom' : String(preset)}
          onChange={(e) =>
            setPreset(e.target.value === 'custom' ? 'custom' : Number(e.target.value))
          }
        >
          {EXPIRY_PRESETS.map((p) => (
            <option key={p.minutes} value={p.minutes}>
              {p.label}
            </option>
          ))}
          <option value="custom">Custom…</option>
        </Select>
        {preset === 'custom' && (
          <>
            <Input
              type="number"
              min={1}
              size="sm"
              value={customValue}
              onChange={(e) => setCustomValue(Number(e.target.value))}
              className="w-16"
            />
            <Select
              size="sm"
              value={customUnit}
              onChange={(e) => setCustomUnit(e.target.value as 'hours' | 'days')}
            >
              <option value="hours">hours</option>
              <option value="days">days</option>
            </Select>
          </>
        )}
      </div>

      {result && (
        <div className="space-y-2 rounded border border-green-200 bg-green-50 p-3 text-xs">
          <div className="text-green-800">
            {result.user_created ? 'Created' : 'Found'} {result.email}
            {result.is_admin && ' · admin'} — link expires{' '}
            {new Date(result.expires_at).toLocaleString()}.
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={result.url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded border border-slate-300 bg-card px-2 py-1 font-mono text-[11px]"
            />
            <Button size="xs" className="shrink-0" onClick={() => void copyLink()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
      {error && <Alert>{error}</Alert>}
    </Card>
  );
}
